import type { RouteLocationRaw, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import customersRoutes from './customers.routes';
import employeesRoutes from './employees.routes';
import bookingsRoutes from './bookings.routes';
import locationsRoutes from '@/routes/locations.routes';
import vendorsRoutes from '@/routes/vendors.routes';
import { Role } from '@/model/role';
import myAccountRoutes from '@/routes/my-account.routes';
import calendarRoutes from '@/routes/calendar.routes';
import { SiteSection, useUiStore } from '@/store/ui-store';
import { useUsersStore } from '@/store/users-store';
import { CalendarResourceType } from '@/store/calendar-store';
import publicRoutes from '@/routes/public.routes';
import VendorPage from '@/components/public/VendorPage.vue';
import i18n, { getAvailableLanguages, Lang } from '@/config/i18n';
import employeeCanSetTimetableGuard from '@/guards/employee-can-set-timetable.guard';
import { isNewVersionDeployed } from '@/helpers/app-version.helper';

declare module 'vue-router' {
  interface RouteMeta {
    // is optional
    public?: boolean;
    // must be declared by every route
    role: Role;
  }
}

// const HomePage = () => import('@/components/pages/HomePage');
const NotFoundPage = () => import('@/components/pages/NotFoundPage.vue');
const PublicNotFoundPage = () =>
  import('@/components/pages/PublicNotFoundPage.vue');
const VendorSignUpPage = () =>
  import('@/components/pages/VendorSignUpPage.vue');
const Login = () => import('@/components/auth/Login.vue');
const SpaceChooser = () => import('@/components/auth/SpaceChooser.vue');
const DeviceSessionChooser = () =>
  import('@/components/auth/DeviceSessionChooser.vue');
const Invitation = () => import('@/components/auth/Invitation.vue');
const ForgotPassword = () => import('@/components/auth/ForgotPassword.vue');
const ResetPassword = () => import('@/components/auth/ResetPassword.vue');
const ForgotPasswordConfirmation = () =>
  import('@/components/auth/ForgotPasswordConfirmation.vue');
const EmailValidation = () => import('@/components/auth/EmailValidation.vue');
const SignUp = () => import('@/components/auth/SignUp.vue');
const Vouchers = () => import('@/components/vouchers/Vouchers.vue');
const Services = () => import('@/components/services/Services.vue');
const Workplaces = () => import('@/components/workplaces/Workplaces.vue');
const Machines = () => import('@/components/machines/Machines.vue');
const Timetables = () => import('@/components/timetables/Timetables.vue');
const CashClosing = () => import('@/components/cash-closing/CashClosing.vue');
const Stats = () => import('@/components/reports/Stats.vue');
const VendorBookings = () => import('@/components/reports/VendorBookings.vue');
const Regions = () => import('@/components/regions/Regions.vue');
const ClosedDates = () => import('@/components/closed-dates/ClosedDates.vue');
const VendorTabAbout = () => import('@/components/public/VendorTabAbout.vue');
const VendorTabServices = () =>
  import('@/components/public/VendorTabServices.vue');
const VendorTabContact = () =>
  import('@/components/public/VendorTabContact.vue');

const indexRoutes: RouteRecordRaw[] = [
  {
    path: '/:lang?/vendor-sign-up',
    name: 'VendorSignUp',
    component: VendorSignUpPage,
    meta: {
      customer: true,
      role: Role.GUEST,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/sessions',
    name: 'DeviceSessions',
    component: DeviceSessionChooser,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/spaces',
    name: 'Spaces',
    component: SpaceChooser,
    meta: {
      role: Role.CUSTOMER,
      customer: true,
    },
  },
  {
    path: '/invitation',
    name: 'Invitation',
    component: Invitation,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Login,
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: SignUp,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/forgot-password-confirmation',
    name: 'ForgotPasswordConfirmation',
    component: ForgotPasswordConfirmation,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: {
      role: Role.GUEST,
      customer: true,
    },
  },
  {
    path: '/email-validation',
    name: 'EmailValidation',
    component: EmailValidation,
    meta: {
      role: Role.GUEST,
      public: true,
    },
  },
  {
    path: '/vouchers',
    name: 'Vouchers',
    component: Vouchers,
    meta: {
      role: Role.STAFF,
    },
  },
  {
    path: '/reports/cash-closing',
    name: 'CashClosing',
    component: CashClosing,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/reports/stats',
    name: 'Stats',
    component: Stats,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/reports/monthly-stats',
    redirect: { name: 'Stats' },
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/reports/vendor-bookings',
    name: 'VendorBookings',
    component: VendorBookings,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/services',
    name: 'Services',
    component: Services,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/workplaces',
    name: 'Workplaces',
    component: Workplaces,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/machines',
    name: 'Machines',
    component: Machines,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/timetables',
    name: 'Timetables',
    component: Timetables,
    beforeEnter: employeeCanSetTimetableGuard,
    meta: {
      role: Role.STAFF,
    },
  },
  {
    path: '/regions',
    name: 'Regions',
    component: Regions,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/closed-dates',
    name: 'ClosedDates',
    component: ClosedDates,
    meta: {
      role: Role.ADMIN,
    },
  },
  {
    path: '/:lang?/:vendor_slug/:location_slug',
    name: 'Vendor',
    component: VendorPage,
    meta: {
      role: Role.GUEST,
      public: true,
    },
    children: [
      {
        path: 'about',
        name: 'VendorTabAbout',
        component: VendorTabAbout,
        meta: {
          role: Role.GUEST,
        },
      },
      {
        path: 'services',
        name: 'VendorTabServices',
        component: VendorTabServices,
        meta: {
          role: Role.GUEST,
        },
      },
      {
        path: 'contact',
        name: 'VendorTabContact',
        component: VendorTabContact,
        meta: {
          role: Role.GUEST,
        },
      },
    ],
  },
];

const routes: RouteRecordRaw[] = [
  ...indexRoutes,
  ...customersRoutes,
  ...employeesRoutes,
  ...bookingsRoutes,
  ...locationsRoutes,
  ...myAccountRoutes,
  ...calendarRoutes,
  ...vendorsRoutes,
  ...publicRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundPage,
    meta: {
      role: Role.STAFF,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PublicNotFound',
    component: PublicNotFoundPage,
    meta: {
      public: true,
      role: Role.GUEST,
    },
  },
];

const STALE_CHUNK_RELOAD_KEY = 'staleChunkReloadedFor';
const STALE_CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/;

const router = createRouter({
  routes,
  history: createWebHistory(import.meta.env.BASE_URL),
  linkExactActiveClass: 'active',
  scrollBehavior(to) {
    const isToAChildRoute = to.matched.length > 1;
    if (isToAChildRoute) {
      return false;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (to, from): Promise<boolean | RouteLocationRaw> => {
  if (await isNewVersionDeployed()) {
    window.location.assign(to.fullPath);
    return false;
  }
  const uiStore = useUiStore();
  if (
    (from.meta.public && !to.meta.public && !to.meta.customer) ||
    (from.meta.customer && !to.meta.public && !to.meta.customer)
  ) {
    uiStore.loadingComponent = true;
  }
  const usersStore = useUsersStore();

  uiStore.setSiteSection(
    to.meta.public
      ? SiteSection.PUBLIC
      : to.meta.customer
        ? SiteSection.CUSTOMER
        : SiteSection.ADMIN,
  );

  // If the :lang parameter is defined in the route
  if (to.matched[0]?.path.includes(':lang')) {
    // If the lang parameter is a valid language code
    if (getAvailableLanguages().includes(to.params.lang as Lang)) {
      // If it is not the current language in i18n
      if (i18n.global.locale.value !== to.params.lang) {
        usersStore.setLanguage(to.params.lang as Lang);
      }
    } else {
      // If the route does not have the lang parameter, we add it
      return {
        name: to.name,
        params: { ...to.params, lang: i18n.global.locale.value },
      };
    }
  }

  const loggedIn = usersStore.isLoggedIn;
  const userRole = usersStore.loggedInUserRole;
  const requiredRole = to.meta.role;
  if (to.name === 'Calendar' && !to.query.viewType) {
    to.query.viewType = CalendarResourceType.EMPLOYEE;
  }
  if (to.name === 'Logout') {
    await usersStore.logout();
    return false;
  }
  if (to.name === 'DeviceSessions' && !usersStore.hasOtherDeviceSessions) {
    return { name: 'Login' };
  }
  if (requiredRole > Role.GUEST) {
    if (loggedIn) {
      if (requiredRole > userRole) {
        uiStore.alert(
          i18n.global.t(
            to.name === 'NotFound'
              ? 'notFound.text'
              : 'guard.insufficientPermissions',
          ),
        );
        if (userRole >= Role.STAFF) {
          return {
            name: 'Calendar',
            query: { viewType: CalendarResourceType.EMPLOYEE },
          };
        }
        return { name: 'MyAccount' };
      } else {
        return true;
      }
    } else {
      // uiStore.alert(router.app.$t('guard.notLoggedIn'));
      return to.name === 'NotFound'
        ? { name: 'PublicNotFound', params: to.params, query: to.query }
        : { name: 'Login', query: { redirect: to.name.toString() } };
    }
  } else {
    return true;
  }
});

router.afterEach(() => {
  const uiStore = useUiStore();
  uiStore.loadingComponent = false;
  sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY);
});

router.onError((error, to) => {
  if (
    !STALE_CHUNK_ERROR.test(error?.message) ||
    sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) === to.fullPath
  ) {
    return;
  }
  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, to.fullPath);
  window.location.assign(to.fullPath);
});

export default router;
